/**
 * useQrTemplate Hook
 *
 * Custom hook for managing QR template state and preview target selection.
 * Provides state management for QR code customization with template persistence
 * to localStorage keyed by site ID.
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { QrTemplate, PreviewTarget, DEFAULT_TEMPLATE } from '../types';
import { loadTemplateWithDefaults, saveTemplate } from '../utils/templateStorage';

/**
 * Return type for the useQrTemplate hook
 */
export interface UseQrTemplateReturn {
  /** Current template state */
  readonly template: QrTemplate;
  /** Update template fields (partial updates supported) */
  readonly updateTemplate: (updates: Partial<QrTemplate>) => void;
  /** Current preview target */
  readonly previewTarget: PreviewTarget;
  /** Change preview target */
  readonly setPreviewTarget: (target: PreviewTarget) => void;
  /** Reset template to defaults */
  readonly resetTemplate: () => void;
}

/**
 * Default preview target - site QR code
 */
const DEFAULT_PREVIEW_TARGET: PreviewTarget = { type: 'site' };

/**
 * Custom hook for managing QR template state and preview target.
 *
 * Features:
 * - Initializes with stored template or default values (Requirement 9.4)
 * - Persists template to localStorage keyed by site ID
 * - Maintains template state across preview target changes (Requirements 9.1, 9.2)
 * - Supports partial template updates (Requirement 9.3)
 *
 * @param siteId - The site ID to key the template storage (optional for backwards compatibility)
 * @returns Object containing template state, update function, preview target, and setter
 *
 * @example
 * ```tsx
 * const { template, updateTemplate, previewTarget, setPreviewTarget } = useQrTemplate('site-123');
 *
 * // Update a single field (auto-persisted)
 * updateTemplate({ color: '#FF0000' });
 *
 * // Update multiple fields (auto-persisted)
 * updateTemplate({ centerEnabled: true, fontSize: 28 });
 *
 * // Change preview target (template persists)
 * setPreviewTarget({ type: 'fixed-bay', bayId: 'bay-1', bayNumber: 1 });
 * ```
 */
export function useQrTemplate(siteId?: string): UseQrTemplateReturn {
  // Track if we've initialized from storage to avoid overwriting on first render
  const initializedRef = useRef(false);

  // Template state - initialized with stored or default values (Requirement 9.4)
  const [template, setTemplate] = useState<QrTemplate>(() => {
    if (siteId) {
      return loadTemplateWithDefaults(siteId);
    }
    return DEFAULT_TEMPLATE;
  });

  // Preview target state - independent from template (Requirements 9.1, 9.2)
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget>(DEFAULT_PREVIEW_TARGET);

  // Re-initialize template when siteId changes
  useEffect(() => {
    if (siteId) {
      const storedTemplate = loadTemplateWithDefaults(siteId);
      setTemplate(storedTemplate);
      initializedRef.current = true;
    }
  }, [siteId]);

  // Persist template to localStorage when it changes
  useEffect(() => {
    // Only save after initial load to avoid overwriting stored data
    if (siteId && initializedRef.current) {
      saveTemplate(siteId, template);
    }
  }, [siteId, template]);

  /**
   * Update template with partial changes.
   * Merges updates with existing template state, preserving unchanged fields.
   * Uses immutable update pattern for predictable state management.
   */
  const updateTemplate = useCallback((updates: Partial<QrTemplate>): void => {
    setTemplate((prevTemplate) => ({
      ...prevTemplate,
      ...updates,
    }));
  }, []);

  /**
   * Reset template to default values.
   * Clears any customizations and persists the reset state.
   */
  const resetTemplate = useCallback((): void => {
    setTemplate(DEFAULT_TEMPLATE);
  }, []);

  return {
    template,
    updateTemplate,
    previewTarget,
    setPreviewTarget,
    resetTemplate,
  };
}
